import * as React from 'react';
import get from 'lodash/get';
import { getPercentageMetricByKey } from '@utils/metrics/metricHelpers';
import { FLOW_METRICS_DISPLAY } from '@utils/flow/meta';
import MetricTrend from './MetricTrend';
import Gauge from './Gauge';
import Trend from './Trend';
import * as Styled from '../styled';
import type { AppFlowMetadata } from '@src/hooks/flow/useFlowConfigs';
import type { FlowTimeseriesDataResponse } from '../hooks';
import { getFlowType } from '../utils';

export interface FlowExperienceProps {
  flowMeta: AppFlowMetadata;
  data: FlowTimeseriesDataResponse;
  onMetricClick: (metricKey: string) => void;
}

const FlowExperience = ({ flowMeta, data, onMetricClick }: FlowExperienceProps) => {
  const { metrics } = flowMeta;
  const successPctMetricKey = getPercentageMetricByKey({ metricKey: metrics.success });
  const handleMetricClick = React.useCallback<React.MouseEventHandler<HTMLDivElement>>(
    (e) => {
      const { metricKey } = (e.currentTarget as HTMLDivElement).dataset;

      if (metricKey) {
        onMetricClick(metricKey);
      }
    },
    [onMetricClick],
  );

  return (
    <Styled.FlowSection $direction="column" style={{ flex: 1.3 }}>
      <Styled.HorizontalMetricContainer
        data-metric-key={successPctMetricKey}
        style={{ flex: 1.5 }}
        onClick={handleMetricClick}
      >
        <Gauge
          settings={flowMeta.definition.settings}
          description={FLOW_METRICS_DISPLAY.success.description}
          value={get(data, ['totals', successPctMetricKey], 0)}
        />
        <Trend
          metricKey={metrics.success}
          data={get(data, ['data', metrics.success], [])}
          style={{ flex: 1 }}
        />
      </Styled.HorizontalMetricContainer>
      <Styled.HorizontalDivider />
      <Styled.HorizontalMetricContainer
        data-metric-key={metrics.avg_complete_duration}
        onClick={handleMetricClick}
      >
        <MetricTrend
          metricKey={metrics.avg_complete_duration}
          name={FLOW_METRICS_DISPLAY.avg_complete_duration.shortName}
          description={FLOW_METRICS_DISPLAY.avg_complete_duration.description}
          data={get(data, ['data', metrics.avg_complete_duration], [])}
          total={get(data, ['totals', metrics.avg_complete_duration], null)}
        />
      </Styled.HorizontalMetricContainer>
      <Styled.HorizontalDivider />
      <Styled.HorizontalMetricContainer
        $notAvailable={!metrics.total_error_count}
        data-metric-key={metrics.total_error_count}
        onClick={handleMetricClick}
      >
        <MetricTrend
          metricKey={metrics.total_error_count}
          name={FLOW_METRICS_DISPLAY.total_error_count.shortName}
          description={FLOW_METRICS_DISPLAY.total_error_count.description}
          data={metrics.total_error_count && get(data, ['data', metrics.total_error_count], [])}
          total={
            metrics.total_error_count && get(data, ['totals', metrics.total_error_count], null)
          }
          placeholder={
            <Styled.MetricPlaceholder style={{ marginLeft: 24 }}>
              <Styled.LinkButton
                href={`/app/activation/workflow/${getFlowType(flowMeta)}/${flowMeta.id}?action=error_event`}
                target="_blank"
              >
                + Configure Error Event
              </Styled.LinkButton>
            </Styled.MetricPlaceholder>
          }
        />
      </Styled.HorizontalMetricContainer>
    </Styled.FlowSection>
  );
};

export default React.memo(FlowExperience);
